import { useOpsApi } from "src/api/client"
import { ApiError } from "src/generated/client"
import { useAuthStore } from "src/stores/auth"
import { useIncidentStore } from "src/stores/incident"
import { useOnCallStore } from "src/stores/oncall"
import { initEnergySave, throttledDelay } from "./energySave"
import { IS_MOCK } from "./opsSystem"

const MINUTE = 60_000

/**
 * App spine init: starts the energy-save pacing and three background poll loops (all
 * `throttledDelay`-paced) — still-logged-in check, on-call status, incident badge counts. Call once
 * from `App.vue` `onMounted`.
 *
 * **Every loop waits before its first run**, because the first one is already somebody else's: the router
 * guard restores the session on boot and `auth.saveLogin` ends by fetching exactly these three. Running
 * eagerly here doubled all of them on every page load — measured against live, one document load: two
 * `auth/me`, two `oncall/current`, four `incident/list` (that one asks twice per refresh, assigned and
 * relevant). Without a session the eager run was a no-op anyway: both store methods return at
 * `authenticated !== true`.
 */
export function startApp(): void {
  initEnergySave()

  if (IS_MOCK) {
    console.info("ops: running in MOCK mode — no real API calls are made")
  }

  void stillLoggedInLoop()
  void onCallLoop()
  void incidentCountLoop()
}

// Periodically re-check the session; a 401 means it expired → lock out. The response also carries
// the deployed frontend version, so we feed it through the new-version check.
async function stillLoggedInLoop(): Promise<void> {
  const auth = useAuthStore()
  for (;;) {
    await throttledDelay(5 * MINUTE, 15 * MINUTE)
    if (auth.authenticated === true) {
      try {
        auth.checkForNewVersion(await useOpsApi().auth.me())
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) auth.lockOut()
        // other errors: ignore (transient)
      }
    }
  }
}

async function onCallLoop(): Promise<void> {
  const onCall = useOnCallStore()
  for (;;) {
    await throttledDelay(1 * MINUTE, 5 * MINUTE)
    try {
      await onCall.updateCurrent()
    } catch (e) {
      console.error("error updating on-call status", e)
    }
  }
}

async function incidentCountLoop(): Promise<void> {
  const incidents = useIncidentStore()
  for (;;) {
    await throttledDelay(1 * MINUTE, 5 * MINUTE)
    try {
      await incidents.updateCounts()
    } catch (e) {
      console.error("error updating incident counts", e)
    }
  }
}
